import React, { useCallback, useEffect, useState } from 'react';
import { planOfRecordService, PlanOfRecordView, PlanRowView } from '../../services/planOfRecordService';

const POLL_MS = 30_000;
const HORIZON: Record<PlanRowView['horizon'], string> = {
  today: 'Today', 'this-week': 'This week', 'this-month': 'This month', later: 'Later', unspecified: '',
};
const STATUS_STYLE: Record<PlanRowView['status'], string> = {
  agreed: 'bg-emerald-50 text-emerald-700',
  proposed: 'bg-amber-50 text-amber-700',
  done: 'bg-slate-100 text-slate-500',
  dropped: 'bg-slate-100 text-slate-400 line-through',
};

interface Props {
  sessionId: string;
}

/** The platform's plan of record (agentic core R1a, WS7 goal X5): decisions and actions as the room makes them. */
const PlanOfRecordPanel: React.FC<Props> = ({ sessionId }) => {
  const [view, setView] = useState<PlanOfRecordView | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await planOfRecordService.get(sessionId);
      setView({ ...next, rows: Array.isArray(next?.rows) ? next.rows : [] });
    } catch {
      /* the panel stays hidden until the view loads */
    }
  }, [sessionId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (!view || view.mode === 'off' || !view.rows.length) return null;

  const section = (kind: PlanRowView['kind'], title: string) => {
    const rows = view.rows.filter((r) => r.kind === kind);
    if (!rows.length) return null;
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{title}</span>
        <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white">
          {rows.map((r) => (
            <li key={r.id} className="flex items-start gap-2 px-3 py-2">
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[r.status]}`}>{r.status}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-navy">{r.text}</p>
                <p className="text-[10px] text-slate-400" title={r.agreedQuote ? `"${r.agreedQuote}"` : undefined}>
                  {[r.owner ? r.owner.name ?? r.owner.role : null, HORIZON[r.horizon], `line ${r.evidenceSeq[0]}`].filter(Boolean).join(' · ')}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 text-left" data-testid="plan-of-record">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Plan of record</span>
        {view.mode === 'shadow' && <span className="text-[10px] font-semibold text-slate-400 uppercase">Shadow</span>}
      </div>
      {section('decision', 'Decisions')}
      {section('action', 'Actions')}
    </div>
  );
};

export default PlanOfRecordPanel;
